import {
  apiDate,
  optional,
  optionalNumber,
  optionalString,
  optionalUuid,
  string,
  uuid,
  z,
  zenum,
  zodLess,
  ZodLessInfer,
} from '@kaiizen/zod-less';
import { makeOptionalPartialMetadataSchema, optionalApiDate, optionalMetadataSchema, optionalNumberOrNumberFromString } from '../schemaLib';
import { ApiOrgPropertySchema } from './ApiOrgPropertySchema';
import { ApiScanSchema } from './ApiScanSchema';

export const PropertyProjectStatusSchema = zenum('active', 'completed');
export type PropertyProjectStatus = ZodLessInfer<typeof PropertyProjectStatusSchema>;

export const ApiPropertyProjectCustomerMetadataSchemaFields = {
  budget: optionalNumberOrNumberFromString.nullable().describe('Budget for the project.'),
  dueDate: optionalApiDate.nullable().describe('Estimated completion date for the project.'),
  projectType: optionalString.nullable().describe("Type of project: 'acquisition', 'disposition', 'turn', 'renovation', or 'training'"),
};

export const STANDARD_PROJECT_TYPES: Record<string, string> = {
  acquisition: 'Acquisition',
  disposition: 'Disposition',
  turn: 'Turn',
  renovation: 'Renovation',
  training: 'Training',
};

export const ApiPropertyProjectCustomerMetadataSchema = zodLess(ApiPropertyProjectCustomerMetadataSchemaFields, { ref: 'propertyProjectCustomerMetadata' });
export type ApiPropertyProjectCustomerMetadata = ZodLessInfer<typeof ApiPropertyProjectCustomerMetadataSchema>;

export const ApiPropertyProjectSchema = zodLess({
  id: uuid,
  createdAt: apiDate,
  updatedAt: apiDate,
  archivedAt: optionalApiDate,
  reactivatedAt: optionalApiDate,
  responsibleUserId: optionalUuid,
  responsibleUserAssignedAt: optionalApiDate,
  orgId: uuid,
  orgPropertyId: uuid,
  name: string,
  description: optionalString,
  status: PropertyProjectStatusSchema,
  customerMetadata: makeOptionalPartialMetadataSchema(ApiPropertyProjectCustomerMetadataSchemaFields),

  managerIds: [uuid],

  // dynamic fields
  firstScan: ApiScanSchema.optional(),
  firstScanId: optionalUuid,
  latestApprovedScan: ApiScanSchema.optional(),
  latestApprovedScanId: optionalUuid,
  latestScan: ApiScanSchema.optional(),
  latestScanId: optionalUuid,
  firstApprovedCloseoutScan: ApiScanSchema.optional(),
  firstApprovedCloseoutScanId: optionalUuid,
  initialApprovedScopeScan: ApiScanSchema.optional(),
  initialApprovedScopeScanId: optionalUuid,
  orgProperty: ApiOrgPropertySchema.optional(),
  scanIds: [uuid],
}, {
  ref: 'propertyProject',
  description: 'Property projects are a way to organize and track scans on a property - typically for the purpose of facilitating a renovation project.',
});

export const ApiActivePropertyProjectSchema = ApiPropertyProjectSchema.extend({
  latitude: optionalNumber.nullable(),
  longitude: optionalNumber.nullable(),
  // daysInProgress: optionalNumber.nullable(),
});

export const ApiCreateScanAsPartOfPropertyProjectSchema = zodLess({
  name: optionalString,
  scanTemplateId: uuid,
  scanTemplateAt: optional(apiDate),
  assignedUserId: optional(uuid),
  customerMetadata: optionalMetadataSchema,
}, {
  ref: 'scan',
  description:
    'Scans are the core unit of work in the KaiPro system. They are created from scan templates and are used to track the progress of a renovation project.',
});
export type ApiCreateScanAsPartOfPropertyProject = ZodLessInfer<typeof ApiCreateScanAsPartOfPropertyProjectSchema>;

export const ApiCreatePropertyProjectSchema = ApiPropertyProjectSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  responsibleUserId: true,
  responsibleUserAssignedAt: true,
  orgId: true,
  latestApprovedScanId: true,
  latestApprovedScan: true,
  scanIds: true,
  firstScanId: true,
  firstScan: true,
  latestScanId: true,
  latestScan: true,
  firstApprovedCloseoutScanId: true,
  firstApprovedCloseoutScan: true,
  initialApprovedScopeScanId: true,
  initialApprovedScopeScan: true,
  orgProperty: true,
  managerIds: true,
}).extend({
  managerIds: optional([uuid]),
  scan: zodLess({
    name: optionalString,
    scanTemplateId: uuid,
    scanTemplateAt: optional(apiDate),
    assignedUserId: optional(uuid),
    customerMetadata: optionalMetadataSchema,
  }),
});
export const ApiUpdatePropertyProjectSchema = ApiCreatePropertyProjectSchema.extend({
  archivedAt: string.nullable().optional(),
  reactivatedAt: string.nullable().optional(),
  responsibleUserId: optionalUuid.nullable(),
  responsibleUserAssignedAt: optionalApiDate.nullable(),
  scopeDueDate: apiDate.nullable().optional().describe('Due date for the current scope shown in Job Info.'),
  scopeDueDateScanId: optionalUuid.describe('Scope receiving scopeDueDate. Must belong to this property project.'),
}).partial();
export const ApiHandoffPropertyProjectSchema = zodLess({
  scanId: uuid,
  recipientUserId: uuid,
  byUserComment: optionalString,
  // Kept while existing clients move to byUserComment.
  comment: optionalString,
});
export const ApiTakePropertyProjectWorkSchema = zodLess({
  scanId: uuid,
});
export type ApiPropertyProject = ZodLessInfer<typeof ApiPropertyProjectSchema>;
export type ApiCreatePropertyProject = ZodLessInfer<typeof ApiCreatePropertyProjectSchema>;
export type ApiUpdatePropertyProject = ZodLessInfer<typeof ApiUpdatePropertyProjectSchema>;
export type ApiHandoffPropertyProject = ZodLessInfer<typeof ApiHandoffPropertyProjectSchema>;
export type ApiTakePropertyProjectWork = ZodLessInfer<typeof ApiTakePropertyProjectWorkSchema>;

const getFixedPropertyProjectQueryKeySegment = (value: string | number | boolean | string[] | undefined) => {
  if (Array.isArray(value)) return value.join(',');
  if (value === undefined) return '';
  return `${value}`;
};

export const getPropertyProjectsQueryKeyPrefix = () => `propertyProjects`;
/** A per-organization realtime key for property-project workflow updates. */
export const getPropertyProjectsRealtimeQueryKey = (orgId: string) => `${getPropertyProjectsQueryKeyPrefix()}/realtime/${orgId}`;
export const getPropertyProjectQueryKey = (propertyProjectId?: string | null): string => `propertyProject/${propertyProjectId ?? ''}`;
export const getPropertyProjectsByOrgPropertyQueryKey = (orgPropertyId?: string | null, options?: { archived?: boolean }): string =>
  `${getPropertyProjectsQueryKeyPrefix()}/by-org-property-id/${orgPropertyId}/${options?.archived ? 'archived' : 'active'}`;
export const getActivePropertyProjectsByMarketIdQueryKey = (orgMarketId?: string) =>
  `${getPropertyProjectsQueryKeyPrefix()}/active-by-market-id/${orgMarketId}`;
export const getActivePropertyProjectsQueryKey = (
  options?: { orgMarketId?: string; limit?: number; search?: string; assignedToCurrentUser?: boolean; sortBy?: string },
) =>
  `${getPropertyProjectsQueryKeyPrefix()}/active/${options?.orgMarketId ?? 'all'}/${options?.limit ?? ''}/${options?.search?.trim() || 'all'}/${
    options?.assignedToCurrentUser ? 'assigned' : 'all'
  }/${options?.sortBy ?? 'updatedAt'}`;
export const getPropertyProjectsQueryKey = (
  propertyProjectQuery: {
    orgMarketIds?: string[];
    orgPropertyId?: string;
    assignedToUserId?: string;
    needsAttention?: boolean;
    search?: string;
    limit?: number;
    archived?: boolean;
    includeArchived?: boolean;
    status?: string;
    sortBy?: string;
  },
) =>
  [
    getPropertyProjectsQueryKeyPrefix(),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.orgMarketIds),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.orgPropertyId),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.assignedToUserId),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.needsAttention),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.search?.trim()),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.limit),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.archived),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.includeArchived),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.status),
    getFixedPropertyProjectQueryKeySegment(propertyProjectQuery.sortBy),
  ].join('/');
export const getActivePropertyProjectsForUserQueryKey = () => `${getPropertyProjectsQueryKeyPrefix()}/active-for-user`;

export const FIND_PROPERTY_PROJECTS_MAX_QUERIES = 500;

export const ApiFindPropertyProjectsSchema = zodLess({
  queries: z.array(string).min(1).max(FIND_PROPERTY_PROJECTS_MAX_QUERIES)
    .describe('Each query is a project id, property id, customer property id, or address.'),
});
export type ApiFindPropertyProjects = ZodLessInfer<typeof ApiFindPropertyProjectsSchema>;

export const PropertyProjectMatchSchema = zenum('projectId', 'propertyId', 'customerPropertyId', 'address', 'partialAddress');
export type PropertyProjectMatch = ZodLessInfer<typeof PropertyProjectMatchSchema>;

export const ApiFoundPropertyProjectSchema = zodLess({
  query: string,
  matchedBy: optional(PropertyProjectMatchSchema).describe('How the query matched a property; absent when nothing matched.'),
  orgProperty: ApiOrgPropertySchema.optional(),
  propertyProject: ApiPropertyProjectSchema.optional().describe("The property's most recent active project, else its most recent archived one."),
  error: optionalString.describe('Why there is no property or project for this query.'),
});
export type ApiFoundPropertyProject = ZodLessInfer<typeof ApiFoundPropertyProjectSchema>;
